import { environment } from '../../environments/environment';
import { ConstantsService } from 'app/services/utilities/constants.service';
import { Component, OnInit, signal, WritableSignal } from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { AutoCompletionType, getUserID } from 'app/util/id-autocompletion.service';
import { LoggedUserStoreService } from 'app/services/store/logged-user-store.service';
import { Router } from '@angular/router';
import { EntityService } from 'app/services/data/entity.service';
import { ManageAccess } from 'app/util/manage-access';
import { LoginService } from 'app/services/data/login.service';
import { GeneralService } from 'app/services/data/general.service';
import { ActionSetStoreService } from 'app/services/store/action-set-store.service';
import { ModuleStoreService } from 'app/services/store/module-store.service';
import { HelpedStoreService } from 'app/services/store/helped-store.service';
import { AlertStoreService } from 'app/services/store/alert-store.service';
import { HelperStoreService } from 'app/services/store/helper-store.service';
import { switchMap } from 'rxjs';

@Component({
    selector: 'app-user-overview',
    templateUrl: './user-overview.component.html',
    styleUrls: ['./user-overview.component.scss']
})
export class UserOverviewComponent implements OnInit {
    backOfficeURL: string = environment.ERP_URL;
    isLoading: WritableSignal<boolean> = signal(false);
    msgErr: string = null;
    showUserView: boolean = false;
    userControl: UntypedFormControl;
    autocompletionType = AutoCompletionType.USER;

    constructor(
        protected loggedUserStoreService: LoggedUserStoreService,
        private loginService: LoginService,
        private manageAccess: ManageAccess,
        private entityService: EntityService,
        private constants: ConstantsService,
        private router: Router,
        private generalService: GeneralService,
        private actionSetStoreService: ActionSetStoreService,
        private moduleStoreService: ModuleStoreService,
        private helpedStoreService: HelpedStoreService,
        private alertStoreService: AlertStoreService,
        private helperStoreService: HelperStoreService

    ) { }

    ngOnInit() {
        this.userControl = new UntypedFormControl();
        this.userControl.valueChanges.subscribe(() => {
            this.msgErr = null;
        });
        if (this.loggedUserStoreService.getLoggedUser()) {
            if (this.loggedUserStoreService.isCoAssistAdmin()) {
                if (!this.showUserView) {
                    this.exitActualUser();
                }
            } else {
                this.manageAccess.redirectUser(this.constants.redirectLink.FIRST_LOGIN);
            }
        } else {
            this.loginService.logOut();
        }
    }

    searchUser() {
        this.msgErr = null;
        this.isLoading.set(true);
        this.entityService.getUser(getUserID(this.userControl.value))
            .pipe(
                switchMap(generalUser => {
                    if (!this.checkAndApplyErrorState(generalUser)) {
                        this._cleanServices();
                        this.loggedUserStoreService.setLoggedUser(generalUser);
                        this.showUserView = true;
                        return this.generalService.fetchData$(generalUser).pipe(
                            switchMap(_ => this.manageAccess.redirectUser(this.constants.redirectLink.FROM_ADMIN))
                        );
                    } else {
                        this.showUserView = false;
                    }
                })
            ).subscribe({
                error: _ => { this.checkAndApplyErrorState() },
                complete: () => {
                    this.isLoading.set(false);
                }
            });
    }

    redirect() {
        window.open(this.backOfficeURL, '_blank');
    }

    globalLogOut() {
        this.exitActualUser().then(_ => {
            this.loginService.logOut();
        });
    }

    exitActualUser() {
        return this.router.navigate(['/administration']).then(_ => {
            this.showUserView = false;
            this.userControl.reset();
            this._cleanServices();
        });
    }

    private _cleanServices() {
        this.actionSetStoreService.clean();
        this.alertStoreService.clean();
        this.helpedStoreService.clean();
        this.helperStoreService.clean();
        this.loggedUserStoreService.resetLoggedUser();
        this.loggedUserStoreService.resetLoggedGroup();
        this.moduleStoreService.clean();
    }

    /**
     * Based on the passed object, determines whether an error state should be returned
     * @param {Object} [res] The object returned by getUser; optional; might be null
     * @returns {boolean} Returns true if there is an error
     */
    private checkAndApplyErrorState(res?: any): boolean {
        if (!res) {
            this.msgErr = 'USER_OVERVIEW.ERROR.NO_USER';
        }
        return !res;
    }
}
